// Registra una vista individual de producto
export const registerProductView = async (productId: string, userId?: string) => {
  const { error } = await supabase
    .from('product_views')
    .insert({ product_id: productId, user_id: userId || null });
  if (error) {
    console.error('Error registrando vista de producto:', error);
  }
};
// Reactivar todos los productos de una tienda
export const reactivateProductsByStoreId = async (storeId: string) => {
  try {
    const { data, error } = await supabase
      .from('products')
      .update({ status: 'active', updated_at: new Date().toISOString() })
      .eq('store_id', storeId);
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('reactivateProductsByStoreId error:', err);
    throw err;
  }
};
// Desactivar todos los productos de una tienda
export const deactivateProductsByStoreId = async (storeId: string) => {
  try {
    const { data, error } = await supabase
      .from('products')
      .update({ status: 'inactive', updated_at: new Date().toISOString() })
      .eq('store_id', storeId);
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('deactivateProductsByStoreId error:', err);
    throw err;
  }
};
import { supabase } from './client';
import { normalizeProfile } from './utils';
import { getProfileById, getProfilesByIds } from './profiles';
import { logActivity } from './activities';
import cache from '../cache/productsCache';

const isTransientNetworkError = (error: unknown): boolean => {
  const message =
    typeof error === 'string'
      ? error
      : (error as { message?: string } | null | undefined)?.message || '';

  const normalized = message.toLowerCase();
  return (
    normalized.includes('load failed') ||
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('network request failed')
  );
};

// Allowed product types enforced by DB check constraint. Keep in sync with DB.
const allowedProductTypes = [
  'book',
  'electronics',
  'vehicle',
  'art',
  'clothing',
  'propiedades',
  'diversion',
  'groceries',
  'tours',
  'clases_particulares',
];
// Incrementa el contador de vistas de un producto
export const incrementProductViewCount = async (productId: string) => {
  // Leer el valor actual
  const { data, error: fetchError } = await supabase
    .from('products')
    .select('view_count')
    .eq('id', productId)
    .single();
  if (fetchError) {
    console.error('Error leyendo view_count en producto:', fetchError);
    return;
  }
  const current = data?.view_count ?? 0;
  const { error } = await supabase
    .from('products')
    .update({ view_count: current + 1 })
    .eq('id', productId);
  if (error) {
    console.error('Error incrementando view_count en producto:', error);
  }
};

function normalizeProductType(t: string | null | undefined): string {
  if (!t) return 'book';
  // Reject non-string inputs early
  if (typeof t !== 'string') return 'book';
  const asStr = t.trim();
  if (!asStr) return 'book';

  const canonical = asStr.toLowerCase();
  // Mapping of known aliases to canonical allowed types
  const aliasMap: Record<string, string> = {
    other: 'groceries',
    almacen: 'groceries',
    'almacén': 'groceries',
    groceries: 'groceries',
    grocery: 'groceries',
    book: 'book',
    books: 'book',
    electronics: 'electronics',
    electronic: 'electronics',
    vehicle: 'vehicle',
    vehicles: 'vehicle',
    art: 'art',
    clothing: 'clothing',
    ropa: 'clothing',
    propiedades: 'propiedades',
    diversion: 'diversion',
    'diversión': 'diversion',
    clases_particulares: 'clases_particulares',
    'clases particulares': 'clases_particulares',
    clases: 'clases_particulares',
    classes: 'clases_particulares',
    'private_classes': 'clases_particulares',
  };

  if (aliasMap[canonical]) return aliasMap[canonical];
  if (allowedProductTypes.includes(canonical)) return canonical;

  console.warn('normalizeProductType: unknown product_type, falling back to "book":', t);
  return 'book';
}

export interface ProductItem {
  id: string;
  store_id?: string | null;
  seller_id: string;
  product_catalog_id?: string | null;
  barcode?: string | null;
  brand?: string | null;
  model?: string | null;
  content?: string | null; // Measurements (gr/mm/cm3) copied from catalog
  title: string;
  author: string | null;
  description: string | null;
  cover_url: string | null;
  fixed_rental_price: number | null;
  fixed_sale_price: number | null;
  nightshift_price?: number | null;
  rental_options?: Array<{ days: number; price: number }>; // New dedicated column for rental options
  free_shipping: boolean;
  weight?: number | null; // Peso en kg para determinar tipo de vehículo de envío
  stock?: number | null;
  status: string;
  created_at: string;
  updated_at: string;
  is_auction: boolean;
  auction_start_time: string | null;
  auction_end_time: string | null;
  starting_bid: number | null;
  current_bid: number | null;
  buy_now_price: number | null;
  highest_bidder_id: string | null;
  product_type: string;
  condition: string | null;
  image_urls: string[];
  main_image_url: string | null;
  details: Record<string, any> | null; // NEW FIELD for dynamic details
  latitude: number | null; // Product location latitude
  longitude: number | null; // Product location longitude
  location_name: string | null; // Product location name
  profiles: {
    username: string;
    avatar_url: string | null;
    karma_points: number;
  } | null;
  stores?: {
    id: string;
    name: string;
    slug: string | null;
    avatar_url: string | null;
    latitude?: number | null;
    longitude?: number | null;
    owner_id?: string | null;
  } | null;
  bidder_id: string;
  amount: number;
  created_at: string;
  profiles: {
    username: string;
    avatar_url: string | null;
  } | null;
}

export const createProduct = async (
  sellerId: string,
  title: string,
  author: string | null,
  description: string | null,
  coverUrl: string | null,
  fixedRentalPrice: number | null,
  fixedSalePrice: number | null,
  freeShipping: boolean,
  isAuction: boolean,
  auctionStartTime: string | null,
  auctionEndTime: string | null,
  startingBid: number | null,
  buyNowPrice: number | null,
  productType: string,
  condition: string | null,
  imageUrls: string[],
  mainImageUrl: string | null,
  status: string = 'available',
  details: Record<string, any> | null, // NEW PARAMETER
  rentalOptions: Array<{ days: number; price: number }> | null = null,
  latitude: number | null = null, // Location latitude
  longitude: number | null = null, // Location longitude
  locationName: string | null = null, // Location name
  storeId: string | null = null
) => {
  // Ensure productType is valid for DB constraints
  productType = normalizeProductType(productType);
  // Build payload conditionally so we don't try to insert columns that may not exist
  const payload: any = {
    seller_id: sellerId,
    // include brand/model if provided via details or explicit params in future
    // brand and model prefer vehicle-specific fields, otherwise fall back to generic details.brand/model
    brand: (details && details.vehicle && details.vehicle.brand) ? details.vehicle.brand : (details && details.brand ? details.brand : undefined),
    model: (details && details.vehicle && details.vehicle.model) ? details.vehicle.model : (details && details.model ? details.model : undefined),
    title,
    author,
    description,
    cover_url: coverUrl,
    fixed_rental_price: fixedRentalPrice,
    fixed_sale_price: fixedSalePrice,
    free_shipping: freeShipping,
    is_auction: isAuction,
    auction_start_time: auctionStartTime,
    auction_end_time: auctionEndTime,
    starting_bid: startingBid,
    current_bid: isAuction ? startingBid : null,
    buy_now_price: buyNowPrice,
    product_type: productType,
    condition: condition,
    image_urls: imageUrls,
    main_image_url: mainImageUrl,
    status,
  };

  // Add details if provided
  if (typeof details !== 'undefined') payload.details = details;

  // If details contains a barcode, promote it to a top-level column when available
  try {
    const maybeBarcode = details && (details.barcode || details['barcode']);
    if (maybeBarcode) payload.barcode = String(maybeBarcode);
  } catch (_) {}

  // If details contains content (measurements like gr/mm/cm3), promote to top-level
  try {
    const maybeContent = details && (details.content || details['content']);
    if (maybeContent) payload.content = String(maybeContent);
  } catch (_) {}

  // If details contains a product_catalog_id, include it at top-level and fetch content/brand/model from catalog
  try {
    const maybeCatalogId = details && (details.product_catalog_id || details['product_catalog_id']);
    if (maybeCatalogId) {
      payload.product_catalog_id = String(maybeCatalogId);
      // Fetch content, brand, model from catalog to copy to products (avoids JOINs on listings)
      try {
        const { data: catalogData, error: catalogError } = await supabase
          .from('product_catalog')
          .select('content, brand, model')
          .eq('id', String(maybeCatalogId))
          .maybeSingle();
        if (!catalogError && catalogData) {
          if ((catalogData as any).content && !payload.content) {
            payload.content = (catalogData as any).content;
          }
          if ((catalogData as any).brand && !payload.brand) {
            payload.brand = (catalogData as any).brand;
          }
          if ((catalogData as any).model && !payload.model) {
            payload.model = (catalogData as any).model;
          }
        }
      } catch (_) {}
    }
  } catch (_) {}

  // If still missing product_catalog_id, try to resolve it from a barcode present in payload.details or payload.barcode
  // Also fetch content to copy to products table (architecture optimization to avoid JOINs)
  if (!payload.product_catalog_id) {
    try {
      const barcodeCandidate = (payload.details && (payload.details.barcode || payload.details?.barcode)) || (payload.barcode || null);
      if (barcodeCandidate) {
        const { data: cat, error: catErr } = await supabase
          .from('product_catalog')
          .select('id, content, brand, model')
          .eq('barcode', String(barcodeCandidate))
          .maybeSingle();
        if (!catErr && cat && (cat as any).id) {
          payload.product_catalog_id = (cat as any).id;
          // Copy content from catalog to avoid JOINs on product listings
          if ((cat as any).content && !payload.content) {
            payload.content = (cat as any).content;
          }
          // Copy brand/model from catalog if not already set
          if ((cat as any).brand && !payload.brand) {
            payload.brand = (cat as any).brand;
          }
          if ((cat as any).model && !payload.model) {
            payload.model = (cat as any).model;
          }
        }
      }
    } catch (_) {
      // ignore resolution errors; product will be created without catalog link
    }

    // Additional fallback: if still missing, try resolving from details.master_product_id (set by uploader flows)
    if (!payload.product_catalog_id) {
      try {
        const masterId = payload.details && (payload.details.master_product_id || payload.details.master_product_id);
        if (masterId) {
          try {
            const { data: masterCatalog, error: masterErr } = await supabase
              .from('product_catalog')
              .select('id, content, brand, model')
              .eq('id', String(masterId))
              .maybeSingle();
            if (!masterErr && masterCatalog && (masterCatalog as any).id) {
              payload.product_catalog_id = (masterCatalog as any).id;
              console.debug('createProduct: resolved product_catalog_id from details.master_product_id ->', payload.product_catalog_id);
              // Copy content from catalog to avoid JOINs on product listings
              if ((masterCatalog as any).content && !payload.content) {
                payload.content = (masterCatalog as any).content;
              }
              // Copy brand/model from catalog if not already set
              if ((masterCatalog as any).brand && !payload.brand) {
                payload.brand = (masterCatalog as any).brand;
              }
              if ((masterCatalog as any).model && !payload.model) {
                payload.model = (masterCatalog as any).model;
              }
            }
          } catch (e) {
            // ignore
          }
        }
      } catch (e) {
        // ignore
      }
    }
  }

  // Only add location columns if they are not null/undefined
  if (latitude !== null && latitude !== undefined) payload.latitude = latitude;
  if (longitude !== null && longitude !== undefined) payload.longitude = longitude;
  if (locationName !== null && locationName !== undefined) payload.location_name = locationName;
  // Associate product to store when provided
  if (typeof storeId !== 'undefined' && storeId !== null) payload.store_id = storeId;
  // Add rental options top-level column if provided
  if (typeof rentalOptions !== 'undefined' && rentalOptions !== null) payload.rental_options = rentalOptions;

  // Add departure_time if present in details (from form flows)
  if (details && (details as any).departure_time) {
    payload.departure_time = (details as any).departure_time;
  } else if (details && details.vehicle && (details.vehicle as any).departure_time) {
    // Fallback for some flows where it might be inside vehicle details
    payload.departure_time = (details.vehicle as any).departure_time;
  }

  // Extract delivery options from details and add as separate columns
  if (details && details.shipping_options) {
    const shippingOpts = details.shipping_options;
    if (typeof shippingOpts.minibid_available !== 'undefined') {
      payload.minibid_available = Boolean(shippingOpts.minibid_available);
    }
    if (typeof shippingOpts.coordinated_local !== 'undefined') {
      payload.coordinated_local = Boolean(shippingOpts.coordinated_local);
    }
    if (typeof shippingOpts.to_be_agreed !== 'undefined') {
      payload.to_be_agreed = Boolean(shippingOpts.to_be_agreed);
    }
  }

  // Defensive: ensure payload.product_type is normalized and present for DB constraint
  if (typeof payload.product_type === 'undefined' || payload.product_type === null) {
    payload.product_type = 'book';
  } else {
    payload.product_type = normalizeProductType(payload.product_type as any);
  }

  // Final defensive check: ensure product_type is in allowed set
  // Ensure product_type is a string; if not, force a safe fallback
  if (typeof payload.product_type !== 'string') {
    const forced = sellerId ? 'groceries' : 'book';
    console.warn('createProduct: product_type is not a string, forcing to', forced, 'original=', payload.product_type);
    payload.product_type = forced;
  } else {
    payload.product_type = String(payload.product_type).trim().toLowerCase();
  }

  if (!allowedProductTypes.includes(payload.product_type)) {
    const fallback = sellerId ? 'groceries' : 'book';
    console.warn('createProduct: product_type not allowed, forcing to "' + fallback + '". original=', payload.product_type);
    payload.product_type = fallback;
  }

  // Diagnostic: log payload keys and type of product_type
  try {
    console.debug('createProduct: payload keys=', Object.keys(payload));
    console.debug('createProduct: product_type ->', payload.product_type, 'typeof ->', typeof payload.product_type);
  } catch (_) {}

  // Log normalized product_type for diagnostics
  try {
  } catch (e) {
    // ignore
  }

  // Diagnostic: log full payload to help trace missing product_catalog_id/barcode
  try {
    console.debug('createProduct: payload=', payload);
  } catch (_) {}

  const { data, error } = await supabase
    .from('products')
    .insert(payload)
    .select()
    .single();

  if (error) {
    // If the insert fails because the DB doesn't have expected columns, provide a clearer message
    console.error('Error creating product:', error, 'payload=', payload);
    if (error.message && /column \"(latitude|longitude|location_name|details)\" does not exist/i.test(error.message)) {
      throw new Error('Error creating product: parece que la tabla `products` no tiene las columnas de ubicación o `details`. Aplica la migración correspondiente en la base de datos. Mensaje original: ' + error.message);
    }
    throw error;
  }
  return data;
};

export const updateProduct = async (
  productId: string,
  sellerId: string,
  updatedFields: {
    store_id?: string | null;
    product_catalog_id?: string | null;
    brand?: string | null;
    model?: string | null;
    title?: string;
    author?: string | null;
    description?: string | null;
    cover_url?: string | null;
    fixed_rental_price?: number | null;
    fixed_sale_price?: number | null;
    nightshift_price?: number | null;
    free_shipping?: boolean;
    is_auction?: boolean;
    auction_start_time?: string | null;
    auction_end_time?: string | null;
    starting_bid?: number | null;
    buy_now_price?: number | null;
    product_type?: string;
    condition?: string | null;
    image_urls?: string[];
    main_image_url?: string | null;
    status?: string;
    details?: Record<string, any> | null; // NEW FIELD
    rental_options?: Array<{ days: number; price: number }> | null;
  }
) => {
  // If caller provided a product_type, normalize it to avoid DB constraint failures
  if (typeof updatedFields.product_type !== 'undefined' && updatedFields.product_type !== null) {
    updatedFields.product_type = normalizeProductType(updatedFields.product_type as any);
  }

  const payload = { ...updatedFields, updated_at: new Date().toISOString() };

  const { data, error } = await supabase
    .from('products')
    .update(payload)
    .eq('id', productId)
    .eq('seller_id', sellerId)
    .select('*, profiles!books_owner_id_fkey(id,username,avatar_url,karma_points)')
    .single();


  if (error) {
    console.error('Error updating product:', error);
    throw error;
  }
  // Explicitly re-query the specific location columns to validate persistence
  try {
    const { data: persisted, error: fetchError } = await supabase
      .from('products')
      .select('latitude,longitude,location_name,rental_options')
      .eq('id', productId)
      .single();

    if (fetchError) {
      console.warn('updateProduct: could not refetch location columns after update:', fetchError);
    } else {
      // Merge persisted values into returned data if present
      if (data && persisted) {
        (data as any).latitude = (persisted as any).latitude;
        (data as any).longitude = (persisted as any).longitude;
        (data as any).location_name = (persisted as any).location_name;
        (data as any).rental_options = (persisted as any).rental_options;
      }
    }
  } catch (err) {
    console.error('updateProduct: error during persistence verification:', err);
  }

  return data;
};

export const getProducts = async (searchTerm: string | null = null): Promise<ProductItem[]> => {
  // Read directly from products table so we ensure `rental_options` is selected
  const selectCols = 'id,store_id,seller_id,title,author,description,cover_url,fixed_rental_price,fixed_sale_price,rental_options,free_shipping,status,created_at,updated_at,is_auction,auction_start_time,auction_end_time,starting_bid,current_bid,buy_now_price,highest_bidder_id,product_type,condition,image_urls,main_image_url,details,latitude,longitude,location_name,profiles!books_owner_id_fkey(id,username,avatar_url,karma_points)';

  let query: any = supabase.from('products').select(selectCols).order('created_at', { ascending: false });
  if (searchTerm && searchTerm.trim() !== '') {
    const term = `%${searchTerm}%`;
    query = query.or(`title.ilike.${term},author.ilike.${term}`);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error fetching products:', error, 'details:', (error as any)?.details, 'code:', (error as any)?.code, 'status:', (error as any)?.status);
    // If the error comes from selecting the related `profiles(...)` or from missing columns,
    // fall back to a safer query without the relational select and then fetch profiles separately.
    try {
      const selectWithoutProfiles = 'id,store_id,seller_id,title,author,description,cover_url,fixed_rental_price,fixed_sale_price,rental_options,free_shipping,status,created_at,updated_at,is_auction,auction_start_time,auction_end_time,starting_bid,current_bid,buy_now_price,highest_bidder_id,product_type,condition,image_urls,main_image_url,details,latitude,longitude,location_name';
      console.warn('getProducts: attempting fallback select without profiles...');
      let fallbackQuery: any = supabase.from('products').select(selectWithoutProfiles).order('created_at', { ascending: false });
      if (searchTerm && searchTerm.trim() !== '') {
        const term = `%${searchTerm}%`;
        fallbackQuery = fallbackQuery.or(`title.ilike.${term},author.ilike.${term}`);
      }
      const { data: fallbackData, error: fallbackError } = await fallbackQuery;
      if (fallbackError) {
        console.error('getProducts: fallback select also failed:', fallbackError);
        throw error; // rethrow original
      }

      // Fetch profiles for distinct seller_ids in one call
      const sellerIds = Array.from(new Set((fallbackData || []).map((p: any) => p.seller_id).filter(Boolean)));
      let profilesMap: Record<string, any> = {};
      if (sellerIds.length > 0) {
        try {
          const profiles = await getProfilesByIds(sellerIds);
          profilesMap = (profiles || []).reduce((acc: any, cur: any) => { acc[cur.id] = cur; return acc; }, {});
        } catch (profilesErr) {
          console.warn('getProducts: could not fetch profiles by ids:', profilesErr);
        }
      }

      const normalizeRentalOptions = (raw: any) => {
        if (raw === null || typeof raw === 'undefined') return null;
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
            return null;
          } catch (e) {
            return null;
          }
        }
        if (typeof raw === 'object') {
          try {
            const vals = Object.values(raw);
            if (Array.isArray(vals) && vals.length > 0) return vals;
          } catch (e) {
            return null;
          }
        }
        return null;
      };

      return (fallbackData || []).map((item: any) => ({
        ...item,
        profiles: normalizeProfile(profilesMap[item.seller_id] ?? null),
        details: item.details,
        latitude: typeof item.latitude !== 'undefined' ? item.latitude : null,
        longitude: typeof item.longitude !== 'undefined' ? item.longitude : null,
        location_name: typeof item.location_name !== 'undefined' ? item.location_name : null,
        rental_options: normalizeRentalOptions(item.rental_options),
      }));
    } catch (diagErr) {
      console.error('getProducts: fallback diagnostic failed:', diagErr);
      throw error;
    }
  }

  // Helper to ensure rental_options is an array or null even if DB returns a JSON string/object
  const normalizeRentalOptions = (raw: any) => {
    if (raw === null || typeof raw === 'undefined') return null;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        return null;
      } catch (e) {
        return null;
      }
    }
    if (typeof raw === 'object') {
      try {
        const vals = Object.values(raw);
        if (Array.isArray(vals) && vals.length > 0) return vals;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  return (data || []).map((item: any) => ({
    ...item,
    profiles: normalizeProfile(item.profiles),
    details: item.details,
    latitude: typeof item.latitude !== 'undefined' ? item.latitude : null,
    longitude: typeof item.longitude !== 'undefined' ? item.longitude : null,
    location_name: typeof item.location_name !== 'undefined' ? item.location_name : null,
    rental_options: normalizeRentalOptions(item.rental_options),
  }));
};

export const getUserProducts = async (userId: string): Promise<ProductItem[]> => {
  const { data: productsData, error } = await supabase
    .from('products')
    .select("id,store_id,seller_id,details->>'product_catalog_id' as product_catalog_id,title,author,description,cover_url,fixed_rental_price,fixed_sale_price,rental_options,free_shipping,status,created_at,updated_at,is_auction,auction_start_time,auction_end_time,starting_bid,current_bid,buy_now_price,highest_bidder_id,product_type,condition,image_urls,main_image_url,details,latitude,longitude,location_name") // Extract product_catalog_id from details JSON when top-level column is absent
    .eq('seller_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching user products:', error);
    throw error;
  }

  const productsWithProfiles: ProductItem[] = [];
  for (const product of productsData) {
    const sellerProfile = await getProfileById(product.seller_id);
    productsWithProfiles.push({
      ...product,
      profiles: sellerProfile ? { username: sellerProfile.username, avatar_url: sellerProfile.avatar_url, karma_points: sellerProfile.karma_points } : null,
    });
  }
  return productsWithProfiles;
};

export const getProductById = async (productId: string): Promise<ProductItem | null> => {
  
  // Check current auth state
  const { data: { session } } = await supabase.auth.getSession();
  if (session) {
  }
  
  try {
    let { data: productData, error } = await supabase
      .from('products')
      .select('*, profiles!books_owner_id_fkey(id,username,avatar_url,karma_points), stores!fk_products_store_id(id,name,slug,avatar_url,owner_id)')
      .eq('id', productId)
      .maybeSingle();

    if (error) {
      if (isTransientNetworkError(error)) {
        console.warn('[products.ts] Network error fetching product by id, returning null:', {
          productId,
          message: (error as any)?.message,
        });
        return null;
      }

      console.warn('[products.ts] getProductById relation query failed, trying fallback', {
        productId,
        code: (error as any)?.code,
        message: (error as any)?.message,
      });
      // Fallback: try fetching without the join in case the join hint (!books_owner_id_fkey) or permissions on profiles/stores is the problem
      const fallback = await supabase
        .from('products')
        .select('*')
        .eq('id', productId)
        .maybeSingle();
      
      if (fallback.error) {
        if (isTransientNetworkError(fallback.error)) {
          console.warn('[products.ts] Network error in fallback fetch, returning null:', {
            productId,
            message: (fallback.error as any)?.message,
          });
          return null;
        }
        console.warn('[products.ts] getProductById fallback query failed', {
          productId,
          code: (fallback.error as any)?.code,
          message: (fallback.error as any)?.message,
        });
        throw fallback.error;
      }
      productData = fallback.data;
    }

    if (!productData) {
      console.warn('[products.ts] No product found for ID:', productId);
      return null;
    }


    // Normalizar perfiles y tiendas
    let sellerProfile = (productData as any).profiles;
    if (Array.isArray(sellerProfile)) sellerProfile = sellerProfile[0];
    
    let storeData = (productData as any).stores;
    if (Array.isArray(storeData)) storeData = storeData[0];

    // Fallbacks si el join falló
    if (!sellerProfile && productData.seller_id) {
      try {
        sellerProfile = await getProfileById(productData.seller_id);
      } catch (e) {
        if (!isTransientNetworkError(e)) {
          console.warn('[products.ts] Fallback profile fetch failed:', e);
        }
      }
    }

    if (!storeData && productData.store_id) {
      try {
        const { data: s } = await supabase.from('stores').select('id,name,slug,avatar_url,owner_id').eq('id', productData.store_id).maybeSingle();
        if (s) storeData = s;
      } catch (e) {
        if (!isTransientNetworkError(e)) {
          console.warn('[products.ts] Fallback store fetch failed:', e);
        }
      }
    }

    // Normalize rental_options
    const normalizeRentalOptions = (raw: any) => {
      if (!raw) return null;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : null;
        } catch (e) { return null; }
      }
      return null;
    };

    const finalProduct = {
      ...productData,
      profiles: sellerProfile || null,
      stores: storeData || null,
      rental_options: normalizeRentalOptions((productData as any).rental_options),
      fixed_sale_price: productData.fixed_sale_price ?? null,
      nightshift_price: (productData as any).nightshift_price ?? null,
      stock: (productData as any).stock ?? null,
    };


    return finalProduct;
  } catch (err) {
    if (isTransientNetworkError(err)) {
      console.warn('[products.ts] Transient network error in getProductById, returning null:', {
        productId,
        message: (err as any)?.message,
      });
      return null;
    }
    console.warn('[products.ts] getProductById failed', {
      productId,
      code: (err as any)?.code,
      message: (err as any)?.message || String(err),
    });
    return null; // Return null instead of throwing to avoid crashing the caller
  }
};

// Find products referencing a barcode stored in details->>'barcode' or top-level barcode if it exists
export const getProductsByBarcode = async (barcode: string): Promise<any[]> => {
  try {
    // Try top-level barcode column first
    console.log('[getProductsByBarcode] Buscando con barcode top-level:', barcode);
    const { data, error } = await supabase
      .from('products')
      .select('id,seller_id,title,product_type,condition,image_urls,main_image_url,details,barcode,brand,content')
      .eq('barcode', barcode);

    if (error) {
      console.warn('[getProductsByBarcode] Top-level barcode filter error:', error);
    }

    if (data && data.length > 0) {
      console.log('[getProductsByBarcode] Encontrado por barcode top-level:', data.length, 'resultado(s)');
      return data;
    }

    // Fallback: try JSONB path
    console.log('[getProductsByBarcode] Intentando JSONB details->barcode');
    const { data: jsonbData, error: jsonbError } = await supabase
      .from('products')
      .select('id,seller_id,title,product_type,condition,image_urls,main_image_url,details,barcode,brand,content')
      .filter("details->>barcode", 'eq', barcode);

    if (jsonbError) {
      console.warn('[getProductsByBarcode] JSONB filter error:', jsonbError);
    }

    if (jsonbData && jsonbData.length > 0) {
      console.log('[getProductsByBarcode] Encontrado por JSONB:', jsonbData.length, 'resultado(s)');
      return jsonbData;
    }

    console.log('[getProductsByBarcode] No encontrado en ninguna ubicación');
    return [];
  } catch (err) {
    console.error('[getProductsByBarcode] Catch error:', err);
    return [];
  }
};

// Catalog-focused helpers (used if DB migration applied)
export const getCatalogByBarcode = async (barcode: string): Promise<any | null> => {
  try {
    console.log('[getCatalogByBarcode] Buscando en product_catalog:', barcode);
    const { data, error } = await supabase
      .from('product_catalog')
      .select('*')
      .eq('barcode', barcode)
      .maybeSingle();
    if (error) {
      console.error('[getCatalogByBarcode] Error completo:', {
        code: (error as any).code,
        message: (error as any).message,
        status: (error as any).status,
        details: error
      });
      console.warn('getCatalogByBarcode error', error);
      return null;
    }
    console.log('[getCatalogByBarcode] Resultado:', data ? 'encontrado' : 'no encontrado');
    return data || null;
  } catch (err) {
    console.error('getCatalogByBarcode catch error', err);
    return null;
  }
};

export const getCatalogById = async (catalogId: string): Promise<any | null> => {
  try {
    const { data, error } = await supabase
      .from('product_catalog')
      .select('*')
      .eq('id', catalogId)
      .maybeSingle();
    if (error) {
      if ((error as any).code === 'PGRST116') return null;
      console.warn('getCatalogById error', error);
      return null;
    }
    return data || null;
  } catch (err) {
    console.error('getCatalogById error:', err);
    return null;
  }
};

/**
 * Unified search across product pool (catalog + products)
 * Used for quick product loading to prefill forms automatically
 * 
 * Search strategy:
 * 1. Catalog (global source of truth)
 * 2. Existing products table
 * 
 * @returns { source, data, prefilled, hasMatch } or null if not found
 */
export const searchProductInPool = async (barcode: string) => {
  if (!barcode || !String(barcode).trim()) {
    return null;
  }

  const normalizedBarcode = String(barcode).trim();

  try {
    console.log('[searchProductInPool] Iniciando búsqueda para:', normalizedBarcode);
    
    // Step 1: Search in collaborative_product_pool (main source)
    console.log('[searchProductInPool] Buscando en collaborative_product_pool');
    const { data: poolData, error: poolError } = await supabase
      .from('collaborative_product_pool')
      .select('barcode,title,brand,product_type,groceries_subcategory,content,image_urls,main_image_url')
      .eq('barcode', normalizedBarcode);
    
    if (poolError) {
      console.warn('[searchProductInPool] Pool search error:', poolError);
    } else if (poolData && poolData.length > 0) {
      const bestMatch = poolData[0];
      console.log('[searchProductInPool] Encontrado en pool:', bestMatch.title);
      return {
        source: 'pool',
        data: bestMatch,
        prefilled: {
          barcode: bestMatch.barcode || normalizedBarcode,
          title: bestMatch.title || '',
          brand: bestMatch.brand || '',
          product_type: bestMatch.product_type || 'groceries',
          groceries_subcategory: bestMatch.groceries_subcategory || '',
          content: bestMatch.content || '',
          image_urls: bestMatch.image_urls || [],
          main_image_url: bestMatch.main_image_url || null,
        },
        hasMatch: true,
      };
    }

    // Step 2: Search in catalog (global source of truth)
    console.log('[searchProductInPool] Buscando en product_catalog');
    const catalogMatch = await getCatalogByBarcode(normalizedBarcode);
    
    if (catalogMatch) {
      console.log('[searchProductInPool] Encontrado en catalog');
      return {
        source: 'catalog',
        data: catalogMatch,
        prefilled: {
          barcode: catalogMatch.barcode,
          title: catalogMatch.title || '',
          brand: catalogMatch.brand || '',
          product_type: catalogMatch.product_type || 'groceries',
          model: catalogMatch.model || '',
          content: catalogMatch.content || '',
          details: catalogMatch.details || null,
          image_urls: (catalogMatch as any).image_urls || [],
          main_image_url: (catalogMatch as any).main_image_url || null,
        },
        hasMatch: true,
      };
    }

    // Step 3: Search in products table (existing inventory)
    console.log('[searchProductInPool] Buscando en products table');
    const productMatches = await getProductsByBarcode(normalizedBarcode);
    
    if (productMatches && productMatches.length > 0) {
      const bestMatch = productMatches[0];
      console.log('[searchProductInPool] Encontrado en products:', bestMatch.title);
      return {
        source: 'products',
        data: bestMatch,
        prefilled: {
          barcode: bestMatch.barcode || normalizedBarcode,
          title: bestMatch.title || '',
          brand: bestMatch.brand || bestMatch.details?.brand || '',
          product_type: bestMatch.product_type || 'groceries',
          model: bestMatch.details?.model || '',
          content: bestMatch.content || bestMatch.details?.content || '',
          details: bestMatch.details || null,
          image_urls: bestMatch.image_urls || [],
          main_image_url: bestMatch.main_image_url || null,
        },
        hasMatch: true,
      };
    }

    // Not found in any source
    console.log('[searchProductInPool] Barcode no encontrado en ninguna fuente:', normalizedBarcode);
    return null;
  } catch (err) {
    console.error('searchProductInPool error:', err);
    return null;
  }
};

/**
 * DEBUG: Get sample products to inspect structure
 */
export const debugGetSampleProducts = async (limit: number = 5): Promise<any[]> => {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id,title,barcode,brand,content,details,product_type')
      .limit(limit);
    
    if (error) {
      console.error('[debugGetSampleProducts] Error:', error);
      return [];
    }
    
    console.log('[debugGetSampleProducts] Productos encontrados:', data?.length);
    if (data && data.length > 0) {
      console.log('[debugGetSampleProducts] Primero:', JSON.stringify(data[0], null, 2));
    }
    
    return data || [];
  } catch (err) {
    console.error('[debugGetSampleProducts] Catch error:', err);
    return [];
  }
};

/**
 * DEPRECATED: product_availability table was removed in architecture renovation.
 * Stock and price are now stored directly on the products table.
 * This function returns null for backwards compatibility.
 */
export const getProductAvailability = async (catalogId: string, sellerId: string): Promise<{ price: number | null; stock: number | null } | null> => {
  // Table no longer exists - return null
  return null;
};

export const createCatalogEntry = async (payload: { title?: string; brand?: string; model?: string; barcode?: string; product_type?: string; content?: string; details?: any; image_urls?: string[]; main_image_url?: string | null; }) => {
  try {
    // Normalize product_type when creating catalog entries to keep in sync with products constraints
    if (typeof payload.product_type !== 'undefined' && payload.product_type !== null) {
      payload.product_type = normalizeProductType(payload.product_type as any);
    }
    // Use upsert on barcode to avoid duplicate-key errors when two clients
    // attempt to create the same catalog entry concurrently.
    // If `barcode` is undefined, fallback to insert.
    if (payload.barcode) {
      const { data, error } = await supabase
        .from('product_catalog')
        .upsert(payload, { onConflict: ['barcode'] })
        .select()
        .single();
      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('product_catalog')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    }
  } catch (err) {
    console.error('createCatalogEntry error', err);
    // If a unique-constraint violation slipped through, try to fetch by barcode
    try {
      const barcode = (payload as any).barcode;
      if (barcode) {
        const existing = await getCatalogByBarcode(barcode);
        if (existing) return existing;
      }
    } catch (fetchErr) {
      console.warn('createCatalogEntry: fetch after insert-error failed', fetchErr);
    }
    throw err;
  }
};

/**
 * Search products across the shared catalog and fallback to seller products.
 * Returns an array of lightweight product suggestions.
 */
export const searchProducts = async (q: string, limit: number = 12): Promise<any[]> => {
  if (!q || q.trim().length < 1) return [];
  const term = `%${q.trim()}%`;
  try {
    // Prefer the centralized catalog first
    const { data: catalogResults, error: catalogErr } = await supabase
      .from('product_catalog')
      .select('id,title,brand,product_type,barcode,image_urls,main_image_url')
      .or(`title.ilike.${term},brand.ilike.${term}`)
      .limit(limit);

    if (catalogErr) {
      console.warn('searchProducts: catalog query failed, falling back to products table', catalogErr);
    }

    if (catalogResults && catalogResults.length > 0) return catalogResults;

    // Fallback: search the user's products table if catalog empty or missing
    const { data: prodResults, error: prodErr } = await supabase
      .from('products')
      .select('id,title,brand,product_type,image_urls,main_image_url')
      .or(`title.ilike.${term},brand.ilike.${term}`)
      .limit(limit);

    if (prodErr) {
      console.error('searchProducts fallback error:', prodErr);
      return [];
    }
    return prodResults || [];
  } catch (err) {
    console.error('searchProducts error:', err);
    return [];
  }
};

export const getProductsByStoreId = async (storeId: string): Promise<ProductItem[]> => {
  try {
    const baseCols = 'id,store_id,seller_id,title,author,description,cover_url,fixed_rental_price,fixed_sale_price,rental_options,free_shipping,status,created_at,updated_at,is_auction,auction_start_time,auction_end_time,starting_bid,current_bid,buy_now_price,highest_bidder_id,product_type,groceries_subcategory,condition,image_urls,main_image_url,details,latitude,longitude,location_name';
    const selectColsWithProfile = `${baseCols},profiles!books_owner_id_fkey(id,username,avatar_url,karma_points)`;

    let data: any[] | null = null;
    let error: any = null;

    ({ data, error } = await supabase
      .from('products')
      .select(selectColsWithProfile)
      .eq('store_id', storeId)
      .order('created_at', { ascending: false }));

    // Some environments don't expose this join relation consistently; fallback to base select.
    if (error) {
      console.warn('[getProductsByStoreId] join query failed, retrying without profile join:', error, 'storeId:', storeId);
      ({ data, error } = await supabase
        .from('products')
        .select(baseCols)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false }));
    }

    if (error) {
      console.error('[getProductsByStoreId] error:', error, 'storeId:', storeId);
      throw error;
    }
    const normalizeRentalOptions = (raw: any) => {
      if (raw === null || typeof raw === 'undefined') return null;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) return parsed;
          return null;
        } catch (e) {
          return null;
        }
      }
      if (typeof raw === 'object') {
        try {
          const vals = Object.values(raw);
          if (Array.isArray(vals) && vals.length > 0) return vals;
        } catch (e) {
          return null;
        }
      }
      return null;
    };

    const result = (data || []).map((item: any) => ({
      ...item,
      profiles: normalizeProfile(item.profiles),
      details: item.details ?? null,
      latitude: typeof item.latitude !== 'undefined' ? item.latitude : null,
      longitude: typeof item.longitude !== 'undefined' ? item.longitude : null,
      location_name: typeof item.location_name !== 'undefined' ? item.location_name : null,
      rental_options: normalizeRentalOptions(item.rental_options),
    }));
    return result;
  } catch (err) {
    console.error('[getProductsByStoreId] exception:', err, 'storeId:', storeId);
    throw err;
  }
};

export const updateCatalogEntry = async (catalogId: string, payload: { title?: string; brand?: string; model?: string; barcode?: string; product_type?: string; content?: string; details?: any; image_urls?: string[]; main_image_url?: string | null; }) => {
  try {
    if (typeof payload.product_type !== 'undefined' && payload.product_type !== null) {
      payload.product_type = normalizeProductType(payload.product_type as any);
    }
    const { data, error } = await supabase
      .from('product_catalog')
      .update({ ...payload, updated_at: new Date().toISOString() })
      .eq('id', catalogId)
      .select()
      .single();
    if (error) throw error;
    return data;
  } catch (err) {
    console.error('updateCatalogEntry error', err);
    throw err;
  }
};

export const addProductAvailability = async (catalogId: string, sellerId: string, opts: { price?: number | null; stock?: number | null; expires_at?: string | null; status?: string | null; shipping_options?: any | null } ) => {
  try {
    const payload: any = {
      product_catalog_id: catalogId,
      seller_id: sellerId,
      price: typeof opts.price !== 'undefined' ? opts.price : null,
      stock: typeof opts.stock !== 'undefined' ? opts.stock : 0,
      expires_at: opts.expires_at ?? null,
      status: opts.status ?? 'available',
      shipping_options: opts.shipping_options ?? null,
    };

    // DEPRECATED: product_availability table was removed.
    // Price and stock are now stored directly on products table via createProduct.
    // Return a mock object for backwards compatibility.
    console.warn('addProductAvailability: product_availability table no longer exists. Price/stock should be set on products directly.');
    return payload;
  } catch (err) {
    console.error('addProductAvailability error', err);
    // Don't throw - just return null for backwards compatibility
    return null;
  }
};

export interface ProductFilterOptions {
  q?: string | null;
  category?: string | null;
  min_price?: number | null;
  max_price?: number | null;
  lat?: number | null;
  lng?: number | null;
  max_km?: number | null;
  date_filter?: string | null; // '24h' | '7d' | '30d' | '365d' | null
  limit?: number;
  offset?: number;
  seller_id?: string | null;
}

/**
 * Server-side filtered product fetch using Supabase RPC.
 * Returns an array of ProductItem-like objects (profiles embedded as `profiles` JSON).
 */
export const getProductsFiltered = async (opts: ProductFilterOptions = {}): Promise<ProductItem[]> => {
  try {
    const rpcParams: any = {
      q: opts.q ?? null,
      category: opts.category ?? null,
      min_price: opts.min_price ?? null,
      max_price: opts.max_price ?? null,
      lat: opts.lat ?? null,
      lng: opts.lng ?? null,
      max_km: opts.max_km ?? null,
      date_filter: opts.date_filter ?? null,
      seller_id: opts.seller_id ?? null,
      // The RPC function signature in the DB expects p_limit/p_offset (avoid reserved names)
      p_limit: opts.limit ?? 50,
      p_offset: opts.offset ?? 0,
    };


    const cacheKey = await cache.generateCacheKey(rpcParams);

    // If offline, return cached page if available
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      try {
        const cached = await cache.getCachedProducts(cacheKey);
        if (cached) {
          return cached as ProductItem[];
        }
      } catch (e) {
        console.warn('[CACHE] Error reading cache while offline:', e);
      }
    }

    const { data, error } = await supabase.rpc('rpc_get_products', rpcParams);
    if (error) {
      console.error('getProductsFiltered RPC error:', {
        message: (error as any)?.message ?? error,
        details: (error as any)?.details ?? null,
        hint: (error as any)?.hint ?? null,
        code: (error as any)?.code ?? null,
      });
      throw error;
    }
    const rowIds = Array.isArray(data) ? (data as any).map((r: any) => r.id) : [];
    // data will be an array of json objects (each is product + profiles)
    const rows: any[] = (data as any) || [];
    // Cache server response (page) for later offline use and prune if needed
    try {
      await cache.setCachedProducts(cacheKey, rows, { ttl: 1000 * 60 * 60 * 24 /* 24h */, maxEntries: 500, maxBytes: 5_000_000 });
    } catch (e) {
      console.warn('[CACHE] Error storing products page:', e);
    }

    // Normalize to ProductItem shape expected by the UI
    return rows.map((r) => ({
      id: r.id,
        store_id: r.store_id ?? null,
      seller_id: r.seller_id,
      brand: r.brand ?? null,
      model: r.model ?? null,
      title: r.title,
      author: r.author ?? null,
      description: r.description ?? null,
      cover_url: r.cover_url ?? null,
      fixed_rental_price: r.fixed_rental_price ?? null,
      fixed_sale_price: r.fixed_sale_price ?? null,
      nightshift_price: r.nightshift_price ?? null,
      rental_options: r.rental_options ?? null,
      free_shipping: r.free_shipping ?? false,
      status: r.status,
      created_at: r.created_at,
      updated_at: r.updated_at,
      is_auction: r.is_auction,
      auction_start_time: r.auction_start_time ?? null,
      auction_end_time: r.auction_end_time ?? null,
      starting_bid: r.starting_bid ?? null,
      current_bid: r.current_bid ?? null,
      buy_now_price: r.buy_now_price ?? null,
      highest_bidder_id: r.highest_bidder_id ?? null,
      product_type: r.product_type,
      condition: r.condition ?? null,
      image_urls: r.image_urls ?? [],
      main_image_url: r.main_image_url ?? null,
      details: r.details ?? null,
      latitude: typeof r.latitude !== 'undefined' ? r.latitude : null,
      longitude: typeof r.longitude !== 'undefined' ? r.longitude : null,
      location_name: r.location_name ?? null,
      profiles: r.profiles ?? null,
    }));
  } catch (err) {
    console.error('getProductsFiltered error:', err);
    throw err;
  }
};

export const placeBid = async (productId: string, bidderId: string, amount: number) => {
  const { data: product, error: fetchError } = await supabase
    .from('products')
    .select('current_bid, auction_end_time, seller_id, title, buy_now_price, highest_bidder_id')
    .eq('id', productId)
    .single();

  if (fetchError || !product) {
    console.error('Error fetching product for bid:', fetchError);
    throw new Error('No se pudo obtener la información del producto para la puja.');
  }

  if (product.seller_id === bidderId) {
    throw new Error('No podés pujar por tus propios productos.');
  }

  if (new Date(product.auction_end_time!) < new Date()) {
    throw new Error('La subasta ha terminado.');
  }

  // --- Lógica para "Comprar Ahora" ---
  if (product.buy_now_price && amount >= product.buy_now_price) {
    const { error: updateProductError } = await supabase
      .from('products')
      .update({
        current_bid: product.buy_now_price,
        highest_bidder_id: bidderId,
        status: 'sold',
        auction_end_time: new Date().toISOString(),
      })
      .eq('id', productId);

    if (updateProductError) {
      console.error('Error updating product for buy now:', updateProductError);
      throw new Error('No se pudo completar la compra inmediata. Inténtalo de nuevo.');
    }

    await logActivity(
      bidderId,
      'placed_bid',
      `Compraste "${product.title}" por compra inmediata de ${new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(product.buy_now_price)}.`,
      `/products/${productId}`,
      product.seller_id
    );

    return { message: 'Compra inmediata realizada con éxito.' };
  }
  // --- Fin de la lógica para "Comprar Ahora" ---

  const minBidIncrement = 100;
  if (amount < (product.current_bid || 0) + minBidIncrement) {
    throw new Error(`Tu puja debe ser al menos ${new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format((product.current_bid || 0) + minBidIncrement)} (es decir, $${minBidIncrement} superior a la puja actual).`);
  }

  if (amount % 100 !== 0) {
    throw new Error('Tu puja debe ser un múltiplo de $100.');
  }

  const { data: newBid, error: bidError } = await supabase
    .from('bids')
    .insert({ product_id: productId, bidder_id: bidderId, amount: amount })
    .select()
    .single();

  if (bidError) {
    console.error('Error placing bid:', bidError);
    throw new Error('No se pudo registrar tu puja. Inténtalo de nuevo.');
  }

  const { data: updateData, error: updateError } = await supabase
    .from('products')
    .update({ current_bid: amount, highest_bidder_id: bidderId })
    .eq('id', productId);


  if (updateError) {
    console.error('Error updating product with new bid:', updateError);
    throw new Error('Tu puja se registró, pero hubo un problema al actualizar el producto. Contacta al soporte.');
  }

  await logActivity(
    bidderId,
    'placed_bid',
    `Pujaste ${new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount)} por "${product.title}".`,
    `/products/${productId}`,
    product.seller_id
  );

  return newBid;
};

export const getBidsByProductId = async (productId: string): Promise<BidItem[]> => {
  const { data, error } = await supabase
    .from('bids')
    .select('id, product_id, bidder_id, amount, created_at, profiles(username, avatar_url)')
    .eq('product_id', productId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching bids by product ID:', error);
    throw error;
  }

  return data.map((item: any) => ({
    ...item,
    profiles: normalizeProfile(item.profiles),
  }));
};